import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  AlertCircle, Watch, Heart, Smartphone, RefreshCw, 
  CheckCircle2, ArrowRight, Cloud, Settings 
} from "lucide-react";
import { Capacitor } from "@capacitor/core";

interface SamsungHRSetupGuideProps {
  open: boolean;
  onClose: () => void;
  onUseHealthKit?: () => void;
  onUseGoogleFit?: () => void;
  hasHealthKit?: boolean;
  hasGoogleFit?: boolean;
}

export const SamsungHRSetupGuide = ({ 
  open, 
  onClose, 
  onUseHealthKit,
  onUseGoogleFit,
  hasHealthKit = false,
  hasGoogleFit = false
}: SamsungHRSetupGuideProps) => {
  const platform = Capacitor.getPlatform();
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';
  
  // Show native health option based on platform
  const hasNativeHealth = (isIOS && hasHealthKit) || (isAndroid && hasGoogleFit);
  const nativeHealthName = isIOS ? 'Apple Health' : 'Google Fit';
  const onUseNativeHealth = isIOS ? onUseHealthKit : onUseGoogleFit;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-[90%] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-orange-500/10">
            <Watch className="w-6 h-6 text-orange-500" />
          </div>
          <AlertDialogTitle className="text-center">
            Samsung Galaxy Watch / Gear
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4 text-left">
            {/* Recommended method - Google Fit for Samsung on Android */}
            {isAndroid && hasGoogleFit && (
              <div className="p-4 border-2 rounded-lg bg-primary/5 border-primary/30">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-foreground">Método Recomendado</span>
                  <Badge variant="secondary" className="text-xs">Mais Estável</Badge>
                </div>
                <p className="mb-3 text-sm text-muted-foreground">
                  Conecte via Google Fit para dados mais confiáveis do seu Samsung.
                </p>
                <Button
                  onClick={onUseGoogleFit}
                  className="w-full"
                  size="sm"
                >
                  Usar Google Fit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* iOS - Apple Health */}
            {isIOS && hasHealthKit && (
              <div className="p-4 border-2 rounded-lg bg-primary/5 border-primary/30">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-foreground">Método Recomendado</span>
                </div>
                <p className="mb-3 text-sm text-muted-foreground">
                  Se seu Samsung sincroniza com Apple Health, use essa fonte.
                </p>
                <Button
                  onClick={onUseHealthKit}
                  className="w-full"
                  size="sm"
                >
                  Usar Apple Health
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Samsung → Google Fit sync instructions (Android) */}
            {isAndroid && (
              <div className="pt-2">
                <p className="mb-3 text-sm font-medium text-center text-foreground">
                  Configure a sincronização Samsung → Google Fit:
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0">
                      <span className="text-xs font-semibold text-primary">1</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Smartphone className="w-3.5 h-3.5" />
                        <p className="text-sm font-medium">Samsung Health (celular)</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Abra o app <strong>Samsung Health</strong> no celular
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0">
                      <span className="text-xs font-semibold text-primary">2</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Settings className="w-3.5 h-3.5" />
                        <p className="text-sm font-medium">Configurações</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Vá em <strong>Menu → Configurações → Sincronizar com Google Fit</strong>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0">
                      <span className="text-xs font-semibold text-primary">3</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Cloud className="w-3.5 h-3.5 text-green-500" />
                        <p className="text-sm font-medium">Ative a sincronização</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Habilite <strong>Frequência Cardíaca</strong> nas opções de sincronização
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0">
                      <span className="text-xs font-semibold text-primary">4</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Watch className="w-3.5 h-3.5" />
                        <p className="text-sm font-medium">Inicie medição no relógio</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Abra <strong>Samsung Health</strong> no relógio e inicie a medição de FC
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bluetooth fallback instructions */}
            <div className="pt-2">
              <p className="mb-3 text-sm font-medium text-center text-muted-foreground">
                Alternativa: Conexão direta via Bluetooth
              </p>
              
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-2 rounded-lg bg-muted/30">
                  <Heart className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Abra Samsung Health no relógio → Frequência Cardíaca → Mantenha aberto
                  </p>
                </div>
                <div className="flex items-start gap-3 p-2 rounded-lg bg-muted/30">
                  <RefreshCw className="w-4 h-4 shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Volte aqui e tente conectar novamente
                  </p>
                </div>
              </div>
            </div>

            {/* Important note about Samsung */}
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Importante:</strong> Samsung só transmite FC via Bluetooth enquanto 
                Samsung Health está <strong>aberto</strong> no relógio. Para monitoramento 
                contínuo, use Google Fit.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full"
          >
            Entendi, Tentar Novamente
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
